import { FC, useState } from 'react'

import styled from 'styled-components'
import { Controller, useForm } from 'react-hook-form'
import { notifications } from '@mantine/notifications'

import Trash from 'src/Assets/trash.svg?react'
import { colors, device } from 'src/Helpers/constants.ts'
import {
  Tool,
  useAssociateToolWithModelMutation,
  useDissociateToolFromModelMutation,
  useDeleteToolMutation,
  useUpdateToolMutation,
  UpdateToolBody,
} from 'src/Redux/api/toolsApi'
import { useGetAllModelsQuery } from 'src/Redux/api/modelApi'
import { Select } from 'src/Components/Select'
import { numberToMoney } from 'src/Helpers/functions.tsx'
import { IconButton } from 'src/Components/Button/IconButton.tsx'
import LockMinus from 'src/Assets/lock-minus.svg?react'
import LockPlus from 'src/Assets/lock-plus.svg?react'

import { EditToolModal } from './EditToolModal'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  gap: 10px;
  background-color: ${colors.mainGray};
  padding: 10px;
`

const ParametersContainer = styled.div`
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: 1.5fr 0.5fr 3fr 0.5fr;
  align-items: center;
  justify-content: space-between;

  @media ${device.tabletMax} {
    grid-template-columns: 1fr;
    row-gap: 20px;
  }
`

const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-left: auto;

  @media ${device.tabletMax} {
    margin-left: 0;
  }
`

const Parameter = styled.span`
  font-size: 14px;
  width: 100%;
  display: flex;
  color: ${colors.thirdGray};
  font-weight: 600;
  align-items: center;
  justify-content: flex-start;
`

const ToolName = styled(Parameter)`
  cursor: pointer;
`

const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`

const StyledInput = styled(Select)`
  width: 500px;

  @media ${device.tabletMax} {
    width: 100%;
  }
`

const StyledButton = styled(IconButton)`
  background-color: ${colors.fourthBlue} !important;

  &:hover {
    background-color: ${colors.fourthBlue} !important;
  }
  margin-left: auto;
`

const StyledDeleteButton = styled(IconButton)`
  background-color: ${colors.red};

  &:hover {
    background-color: ${colors.red};
  }
  margin-left: auto;
`

const ActionButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`

interface CatalogItemProps {
  tool: Tool
}

interface ToolItemFormData {
  selectedModelId: string
  removedModelId: string
}

export const ToolItem: FC<CatalogItemProps> = ({ tool }) => {
  const { data: modelsResponse } = useGetAllModelsQuery({
    all: true,
  })
  const [associateToolWithModel] = useAssociateToolWithModelMutation()
  const [dissociateToolFromModel] = useDissociateToolFromModelMutation()
  const [deleteTool] = useDeleteToolMutation()
  const [updateTool] = useUpdateToolMutation()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const { control, handleSubmit, reset } = useForm<ToolItemFormData>()

  if (!tool) {
    return null
  }

  const { name, modelTools, toolWorkPrice, condition, description } = tool

  const onAddModelClick = async (data: ToolItemFormData): Promise<void> => {
    try {
      await associateToolWithModel({
        modelId: Number(data?.selectedModelId),
        toolId: tool.id,
      })
        .unwrap()
        .then(() => {
          notifications.show({
            title: 'Поддерживаемая модель добавлена',
            message: '',
            position: 'top-right',
          })
          reset()
        })
    } catch {
      // Ошибки обрабатываются RTK Query
    }
  }

  const onRemoveModelClick = async (data: ToolItemFormData): Promise<void> => {
    try {
      await dissociateToolFromModel({
        modelId: Number(data?.removedModelId),
        toolId: tool.id,
      })
        .unwrap()
        .then(() => {
          notifications.show({
            title: 'Поддерживаемая модель удалена',
            message: '',
            position: 'top-right',
          })
          reset()
        })
    } catch {
      // Ошибки обрабатываются RTK Query
    }
  }

  const onDeleteToolClick = async (): Promise<void> => {
    try {
      await deleteTool(tool.id)
        .unwrap()
        .then(() => {
          notifications.show({
            title: 'Навесное оборудование успешно удалено',
            message: '',
            position: 'top-right',
          })
        })
    } catch {
      // Ошибки обрабатываются RTK Query
    }
  }

  const onEditToolClick = (): void => {
    setIsEditModalOpen(true)
  }

  const onUpdateToolSubmit = async (
    toolData: UpdateToolBody,
  ): Promise<void> => {
    try {
      await updateTool({
        id: tool.id,
        data: toolData,
      }).unwrap()
    } catch {
      // Ошибки обрабатываются RTK Query
    }
  }

  const modelToAddOptions =
    modelsResponse?.data
      ?.filter((model) => {
        const isAssigned = Boolean(
          modelTools?.find((modelTool) => {
            return modelTool.model.id === model.id
          }),
        )

        return !isAssigned
      })
      .map((model) => ({
        value: model.id.toString(),
        label: model.name,
      })) || []

  const modelToRemoveOptions =
    modelsResponse?.data
      ?.filter((model) => {
        return Boolean(
          modelTools?.find((modelTool) => {
            return modelTool.model.id === model.id
          }),
        )
      })
      .map((model) => ({
        value: model.id.toString(),
        label: model.name,
      })) || []

  return (
    <Container>
      <ParametersContainer>
        <ToolName onClick={onEditToolClick} data-testid='tool-item-name'>
          {name}
        </ToolName>
        <Parameter>{numberToMoney(toolWorkPrice)}</Parameter>

        <InputsWrapper>
          <InputWrapper>
            <Controller
              name='selectedModelId'
              control={control}
              render={({ field }) => (
                <StyledInput
                  placeholder='Добавить поддерживаемую модель'
                  label='Добавить поддерживаемую модель'
                  data={modelToAddOptions}
                  searchable
                  nothingFoundMessage='Ничего не найдено'
                  {...field}
                  value={field.value || null}
                />
              )}
            />

            <StyledButton
              onClick={handleSubmit(onAddModelClick)}
              tooltiptext={'Добавить поддерживаемую модель'}
              data-testid='add-supported-model-button'
            >
              <LockPlus width={30} height={30} />
            </StyledButton>
          </InputWrapper>

          <InputWrapper>
            <Controller
              name='removedModelId'
              control={control}
              render={({ field }) => (
                <StyledInput
                  placeholder='Удалить поддерживаемую модель'
                  label='Удалить поддерживаемую модель'
                  data={modelToRemoveOptions}
                  searchable
                  nothingFoundMessage='Ничего не найдено'
                  {...field}
                  value={field.value || null}
                />
              )}
            />

            <StyledButton
              onClick={handleSubmit(onRemoveModelClick)}
              tooltiptext={'Удалить поддерживаемую модель'}
              data-testid='remove-supported-model-button'
            >
              <LockMinus width={30} height={30} />
            </StyledButton>
          </InputWrapper>
        </InputsWrapper>
        <ActionButtonsWrapper>
          <StyledDeleteButton
            onClick={onDeleteToolClick}
            tooltiptext={'Удалить оборудование'}
            data-testid='delete-tool-button'
          >
            <Trash />
          </StyledDeleteButton>
        </ActionButtonsWrapper>
      </ParametersContainer>

      <EditToolModal
        isOpened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onUpdateTool={onUpdateToolSubmit}
        toolId={tool.id}
        images={tool.images}
        initialName={name}
        initialPrice={toolWorkPrice || 0}
        initialCondition={condition}
        initialDescription={description}
      />
    </Container>
  )
}
